import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { QueryState } from '@/shared/ui/QueryState'
import { api } from '@/shared/lib/api'
import { PUBLIC_STALE_TIME_MS } from '@/shared/lib/query'
import { makeLabeler, useCommonCodes } from '@/shared/lib/codes'
import { MobileHome } from '@/public/features/home/ui/HomeVariants'
import { TabletHome } from '@/public/features/home/ui/HomeTablet'
import { HOME_CARD_LIMIT } from '@/public/features/home/constants/homeCards'

export function HomePage() {
  const cards = useQuery({
    queryKey: ['solution-cards', 'home'],
    queryFn: () => api.solutionCards({ limit: HOME_CARD_LIMIT }),
    staleTime: PUBLIC_STALE_TIME_MS,
  })
  const problems = useQuery({
    queryKey: ['problem-cards', 'home'],
    queryFn: () => api.problemCards({ limit: HOME_CARD_LIMIT }),
    staleTime: PUBLIC_STALE_TIME_MS,
  })
  const categories = useCommonCodes('category')
  const categoryLabel = makeLabeler(categories.data)

  return (
    <>
      <MobileHome problems={problems} cards={cards} />
      <TabletHome problems={problems} cards={cards} />

      <div className="desktop-screen">
      <section className="rounded-[10px] border border-white-300 bg-white-50 px-10 py-12">
        <h1>
          동네의 불편함과
          <br />
          해결할 수 있는 기업을 잇습니다
        </h1>
        <p>
          시민이 등록한 생활 문제와 지역 기업의 해결 역량을 하나의 기준으로 정리해, 누가 어떤 문제를
          풀 수 있는지 보여줍니다.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link className="cursor-pointer rounded-lg border border-black-600 bg-black-600 px-[22px] py-[11px] font-semibold text-white-50 disabled:cursor-not-allowed disabled:opacity-50" to="/problems/new">
            문제 등록하기
          </Link>
          <Link className="cursor-pointer rounded-lg border border-black-600 bg-black-600 px-[22px] py-[11px] font-semibold text-white-50 disabled:cursor-not-allowed disabled:opacity-50 border-white-400 bg-white-50 text-black-800" to="/solutions">
            기업카드 보기
          </Link>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3">
          <div className="rounded-[10px] bg-white-200 p-[18px]">
            <b>1. 문제를 등록합니다</b>
            시민이 한 문장으로 불편을 적습니다. 대상과 동네만 함께 고르면 됩니다.
          </div>
          <div className="rounded-[10px] bg-white-200 p-[18px]">
            <b>2. 기업이 해결카드를 등록합니다</b>
            해결 가능한 분야·도움방식·서비스 지역을 시민과 <b>같은 기준</b>으로 입력합니다.
          </div>
          <div className="rounded-[10px] bg-white-200 p-[18px]">
            <b>3. 같은 기준으로 견줍니다</b>
            두 카드가 같은 축으로 정리돼 있어 무엇이 맞고 무엇이 비는지 그대로 드러납니다.
          </div>
        </div>
      </section>

      <h2 className="mt-10 mb-3.5 text-body-md font-bold">최근 등록된 기업카드</h2>
      <QueryState
        isPending={cards.isPending}
        error={cards.error}
        isEmpty={cards.data?.items.length === 0}
        emptyMessage="아직 공개된 기업카드가 없습니다."
      >
        <div className="grid grid-cols-1 gap-3.5 min-[601px]:grid-cols-2 min-[901px]:grid-cols-3">
          {cards.data?.items.map((c) => (
            <Link key={c.id} className="block rounded-[10px] border border-white-300 bg-white-50 p-[18px] transition-[border-color] duration-150 ease-linear hover:border-white-400" to={`/solutions/${c.id}`}>
              <div className="mb-2 text-caption text-white-900">{c.org.name}</div>
              <h3>{c.title}</h3>
              <p>{c.summary}</p>
              <div className="flex flex-wrap gap-[5px]">
                {c.categoryCodes.map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {categoryLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>

      <h2 className="mt-10 mb-3.5 text-body-md font-bold">최근 등록된 시민문제</h2>
      <QueryState
        isPending={problems.isPending}
        error={problems.error}
        isEmpty={problems.data?.items.length === 0}
        emptyMessage="아직 공개된 시민문제가 없습니다."
      >
        <div className="grid grid-cols-1 gap-3.5 min-[601px]:grid-cols-2 min-[901px]:grid-cols-3">
          {problems.data?.items.map((p) => (
            <Link key={p.id} className="block rounded-[10px] border border-white-300 bg-white-50 p-[18px] transition-[border-color] duration-150 ease-linear hover:border-white-400" to={`/problems/${p.id}`}>
              <div className="mb-2 text-caption text-white-900">{p.region.name}</div>
              <h3>{p.title}</h3>
              <div className="flex flex-wrap gap-[5px]">
                {(p.categoryCodes ?? []).map((code) => (
                  <span key={code} className="rounded-full bg-white-200 px-[9px] py-[3px] text-[11px] text-black-500">
                    {categoryLabel(code)}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      </QueryState>
      </div>
    </>
  )
}
